// registerServiceWorker: safe, one-shot registration.
//
// Only registers in production builds (development hot-reload does not
// play well with a caching SW). Skipped in localhost dev. Handles the
// "new version available" case by prompting the SW to skipWaiting
// immediately, then reloading the page once so the operator picks up
// the fresh bundle without a manual refresh.
//
// Safe to call multiple times: the browser dedupes registrations.

export function registerServiceWorker() {
  if (typeof window === "undefined") return;
  if (!("serviceWorker" in navigator)) return;

  // Only in production. NODE_ENV is provided by CRA at build time.
  if (process.env.NODE_ENV !== "production") return;

  // Also skip on localhost so a curious dev session does not get a
  // sticky SW that fights hot reload.
  const host = window.location.hostname;
  const isLocal = host === "localhost" || host === "127.0.0.1" || host === "0.0.0.0";
  if (isLocal) return;

  // Defer until the load event so SW registration never competes with
  // the first paint or the initial data fetch.
  window.addEventListener("load", () => {
    navigator.serviceWorker
      .register("/service-worker.js", { scope: "/" })
      .then((registration) => {
        // Watch for updates. When a new SW is waiting, tell it to
        // activate immediately, then reload the page once.
        registration.addEventListener("updatefound", () => {
          const newSW = registration.installing;
          if (!newSW) return;
          newSW.addEventListener("statechange", () => {
            if (newSW.state === "installed" && navigator.serviceWorker.controller) {
              // A new version is ready. Ask it to take over now.
              newSW.postMessage({ type: "SKIP_WAITING" });
            }
          });
        });

        // Force a one-time reload when the new SW takes control so the
        // page uses the fresh bundle right away.
        let refreshed = false;
        navigator.serviceWorker.addEventListener("controllerchange", () => {
          if (refreshed) return;
          refreshed = true;
          window.location.reload();
        });
      })
      .catch(() => { /* non-fatal: SW is a progressive enhancement */ });
  });
}

// Convenience: also let power users forcibly unregister every active SW
// (e.g. debugging a stuck cache). Exposed as window.__csUnregisterSW() so
// support can walk an operator through it in the console.
if (typeof window !== "undefined") {
  window.__csUnregisterSW = async function () {
    if (!("serviceWorker" in navigator)) return "no sw";
    const regs = await navigator.serviceWorker.getRegistrations();
    await Promise.all(regs.map((r) => r.unregister()));
    const keys = await caches.keys();
    await Promise.all(keys.filter((k) => k.startsWith("cs-")).map((k) => caches.delete(k)));
    return `unregistered ${regs.length}, cleared ${keys.length} caches`;
  };
}
